import type { EventVo } from 'models/event';
import { eventIsParsableNumeric, type EventEntity, type MarketEntity } from 'queries/marketEntityTransform';

/**
 * Chart-shape checks for the event widget, kept free of fetch imports so the client
 * graph stays small.
 */

// Helper function to check if an event has only one market
export const isSingleMarket = (event: EventVo): boolean => {
    const nonHiddenMarkets = event.markets.filter((m) => m.status !== 'invalid');
    return nonHiddenMarkets.length === 1;
};

// Helper function to determine if an event is parsable numeric from server-side data
export const isServerSideNumeric = (event: EventVo): boolean => {
    const nonHiddenMarkets = event.markets.filter((m) => m.status !== 'invalid');
    if (nonHiddenMarkets.length <= 1) {
        return false;
    }

    // Check if all markets have numeric targets
    return nonHiddenMarkets.every((market) => {
        // Check if the market sub-title or title contains numeric patterns
        const titleText = `${market.yes_sub_title || ''} ${market.title || ''} ${market.sub_title || ''}`;

        // Look for numeric patterns in titles (e.g., "At least 4%", "Above $1000", "More than 50")
        const numericPatterns = [
            /\d+\.?\d*%/, // Percentages: 4%, 3.5%
            /\$\d+\.?\d*/, // Dollar amounts: $1000, $50.25
            /\d+\.?\d*\s*(points?|percent|basis|bps)/i, // Points/percent: 100 points, 5 percent
            /(at least|above|below|more than|less than|over|under)\s*\d+/i, // Threshold language
            /\d+\.?\d*\s*(or more|or less|and above|and below)/i, // Range language
        ];

        const hasNumericPattern = numericPatterns.some((pattern) => pattern.test(titleText));

        // Also check custom_strike for explicit numeric fields
        if (market.custom_strike) {
            const customStrike = market.custom_strike as any;
            const numericFields = ['price', 'value', 'number', 'amount', 'count', 'score', 'rating', 'threshold'];
            const hasNumericStrike = numericFields.some(
                (field) => customStrike[field] !== undefined && !isNaN(Number(customStrike[field])),
            );

            if (hasNumericStrike) {
                return true;
            }
        }

        return hasNumericPattern;
    });
};

export type WidgetChartKind = 'single' | 'numeric' | 'categorical';

/**
 * The chart StateManagerGraph renders: classified from the visible market entities once
 * the event entity is in the store (the marketsToRemove kill-switch can hide markets, so
 * a two-market event can render as a single-market chart), from the server's event
 * before that. The history fetches must use this same classification, or a chart can
 * render without the history it reads.
 */
export function widgetChartKind(
    eventEntity: EventEntity | undefined,
    visibleMarkets: MarketEntity[],
    event: EventVo,
): WidgetChartKind {
    const isSingle = eventEntity ? visibleMarkets.length === 1 : isSingleMarket(event);
    if (isSingle) return 'single';
    const isNumeric = eventEntity ? eventIsParsableNumeric(visibleMarkets) : isServerSideNumeric(event);
    return isNumeric ? 'numeric' : 'categorical';
}
